class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.onClickHandler = this.onClickHandler.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.keypressHandler = (event) => {
      if (event.key === "Enter") {
        this.onClickHandler();
      }
    };
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    this.destroy();

    const mode = this.getAttribute("mode") || "on-click";

    if (mode === "on-click") {
      this.addEventListener("click", this.onClickHandler);
      this.addEventListener("keypress", this.keypressHandler);
    } else if (mode === "on-load") {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0,
      });
      this.observer.observe(this);
    }
  }

  destroy() {
    this.removeEventListener("click", this.onClickHandler);
    this.removeEventListener("keypress", this.keypressHandler);

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  onClickHandler() {
    if (
      this.classList.contains("loading") ||
      this.classList.contains("disabled")
    ) {
      return;
    }

    this.classList.add("loading", "disabled");

    const url = this.dataset.url;
    if (url) {
      InfiniteScroll.loadContent(url, this).finally(() => {
        this.classList.remove("loading", "disabled");
      });
    }
  }

  handleIntersection(entries) {
    const entry = entries[0];
    if (entry.isIntersecting) {
      this.observer.unobserve(this);
      this.onClickHandler();
    }
  }

  static getWrapper(element) {
    return element.closest("[data-infinite-scroll-wrapper]");
  }

  static getFetchedWrapper(html, wrapper) {
    if (!wrapper?.id) return null;

    return new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById(wrapper.id);
  }

  static async loadContent(url, triggerElement) {
    const wrapper = InfiniteScroll.getWrapper(triggerElement);
    if (!wrapper) return;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const html = await response.text();

      InfiniteScroll.updateGrid(html, wrapper);
      InfiniteScroll.updatePagination(html, wrapper);
    } catch (error) {
      console.error("Error loading content:", error);
    }
  }

  static updatePagination(html, wrapper) {
    const container = wrapper?.querySelector(".pagination-container");
    const fetchedWrapper = InfiniteScroll.getFetchedWrapper(html, wrapper);
    const pagination = fetchedWrapper?.querySelector(".pagination-container");

    if (pagination && container) {
      container.className = pagination.className;
      container.innerHTML = pagination.innerHTML;
      container.querySelectorAll("infinite-scroll").forEach((element) => {
        element.init();
      });
    } else if (container) {
      container.remove();
    }
  }

  static updateGrid(html, wrapper) {
    const gridContainer = wrapper?.querySelector("[data-infinite-scroll-grid]");
    const fetchedWrapper = InfiniteScroll.getFetchedWrapper(html, wrapper);
    const fetchedGrid = fetchedWrapper?.querySelector(
      "[data-infinite-scroll-grid]",
    );
    const existingGridRow = gridContainer?.querySelector(".grid-row");

    if (!gridContainer || !fetchedGrid || !existingGridRow) return;

    const newItems = fetchedGrid.querySelectorAll(
      ".grid-row > [class*='grid-item-']",
    );
    const firstNewItem = newItems[0];

    newItems.forEach((item) => {
      existingGridRow.appendChild(document.importNode(item, true));
    });

    const paginationElement = wrapper?.querySelector("infinite-scroll");
    const isAutoLoad = paginationElement?.getAttribute("mode") === "on-load";

    if (!isAutoLoad && firstNewItem) {
      setTimeout(() => {
        const focusableElements = getFocusableElements(firstNewItem);
        if (focusableElements?.length) {
          focusableElements[0].focus();
        }
      }, 500);
    }

    if (
      typeof animationCheck !== "undefined" &&
      animationCheck &&
      typeof AOS !== "undefined"
    ) {
      AOS.refreshHard();
    }
  }
}

customElements.define("infinite-scroll", InfiniteScroll);
